import { shell } from '../shell/shellClasses';
import { ActionCard } from './ActionCard';
import type { ActionPriority, DecisionAction, LocalActionFlags } from './types';
import { PRIORITY_LABEL } from './types';

export function PriorityGroup({
  priority,
  actions,
  expanded,
  onToggle,
  flags,
  onPin,
  onBookmark,
  onComplete,
  onDismiss,
  onRestore,
}: {
  priority: ActionPriority;
  actions: DecisionAction[];
  expanded: boolean;
  onToggle: () => void;
  flags: LocalActionFlags;
  onPin: (id: string) => void;
  onBookmark: (id: string) => void;
  onComplete: (id: string) => void;
  onDismiss: (id: string) => void;
  onRestore: (id: string) => void;
}) {
  const panelId = `priority-panel-${priority}`;
  const headingId = `priority-heading-${priority}`;
  const count = actions.length;

  return (
    <section
      className={`rounded-[var(--shell-radius-lg)] border ${shell.border} ${shell.surface}`}
      data-priority-group={priority}
      aria-labelledby={headingId}
    >
      <h2 className="m-0">
        <button
          type="button"
          id={headingId}
          className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left ${shell.focusRing} rounded-t-[var(--shell-radius-lg)]`}
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span className="flex min-h-11 items-center gap-2">
            <span className="text-sm font-semibold text-[var(--shell-fg)]">
              {PRIORITY_LABEL[priority]}
            </span>
            <span
              className={`rounded-full border px-2 py-0.5 text-[11px] tabular-nums ${shell.border} ${shell.muted}`}
            >
              {count}
            </span>
          </span>
          <span className={`text-xs ${shell.subtle}`} aria-hidden>
            {expanded ? 'Collapse' : 'Expand'}
          </span>
        </button>
      </h2>

      <div id={panelId} hidden={!expanded} className="space-y-2 border-t border-[var(--shell-border)] px-3 py-3">
        {count === 0 ? (
          <p className={`px-1 py-2 text-xs ${shell.subtle}`}>No actions in this group.</p>
        ) : (
          actions.map((a) => (
            <ActionCard
              key={a.id}
              action={a}
              pinned={flags.pinned.includes(a.id)}
              bookmarked={flags.bookmarked.includes(a.id)}
              completed={flags.completed.includes(a.id)}
              onPin={() => onPin(a.id)}
              onBookmark={() => onBookmark(a.id)}
              onComplete={() => onComplete(a.id)}
              onDismiss={() => onDismiss(a.id)}
              onRestore={() => onRestore(a.id)}
            />
          ))
        )}
      </div>
    </section>
  );
}
